/**
  * @class mmodAppWall
  * @description
  * Application Wall is integrated part of the user interface
  * used for switching between the modules in mPatrol for mobile phones.
  * It performs the same function as Application Bar, but as a difference,
  * it it ergonomically better suited for small mobile screens and is not visible all the time.
  * Application Wall opens up when user clicks on Application Logo button.
  * Application Wall displays list of available modules and highlights the active one.
  * It also displays information on logged callsign and operational status.
  * User can change here operational status, and language and also perform logout or change password.
  * Module is implemented without workflow, as a pure module named mmodAppWall.
  * Module listens on various messages to update its appearance accordingly:
  * - mmStatus - to update list of modules
  * - mmWindowFocused - to update currently active module
  * - mmUserLogged - used to update callsign of user
  * - mmUsersStatus - used to update operational status of user
  */

import mmodModule from 'mmodModule'
const mfs = require('msysFS')

export default class mmodAppWall extends mmodModule {
  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Creates an instance of the class.
  * @param {string} [id] - ID under which the object will listen for messages
  * @param {string} [cfg] - object initial configuration
  * @param {string} [cfg_schema] - additional schema properties from derived classes
  * @param {string} [dependencies] - additional dependencies from derived classes
  */
  constructor(id, cfg, cfg_schema, dependencies) {
    dependencies = _.union([/* put module specific dependencies here */], dependencies)
    cfg_schema = _.extend({
      init: {
        type: 'object',
        default: { type: 'float', x: '-0', y: 0, width: '0', height: '100%', order: 1 },
        description: 'Initial window setup; same structure as required by `mmWindowSet`',
      },
      behaviors: {
        description: 'comma separated list of behaviors available for switching',
        type: 'array',
        items: { type: 'string', enum: ['desktop', 'dashboard', 'tablet'] },
        default: ['desktop', 'dashboard', 'tablet'],
      },
      modules: {
        description: 'list of modules in application wall',
        type: 'array',
        default: ['mmodMap', 'mmodPlayer', 'mmodIncidents', 'mmodInstructions', 'mmodAlpr', 'mmodScreenings', 'mmodFines', 'mmodUnits', 'mmodChats', 'mmodWiki'],
      },
      admin_modules: {
        description: 'list of admin modules in application wall',
        type: 'array',
        default: [],
      },
      password_minlength: {
        description: 'minimum length for new password',
        default: 6,
        type: 'number',
      },
      always_enabled: {
        description: 'keep enabled even if not run on phone',
        type: 'boolean',
        default: false,
      },
      password_lowercase: { description: 'whether lowercase character is required in new password', type: 'boolean', default: true },
      password_uppercase: { description: 'whether uppercase character is required in new password', type: 'boolean', default: false },
      password_letter: { description: 'whether latin character is required in new password', type: 'boolean', default: false },
      password_digit: { description: 'whether digit is required in new password', type: 'boolean', default: true },
      password_letterdigit: { description: 'whether latin character or digit is required in new password', type: 'boolean', default: false },
      password_symbol: { description: 'whether special symbol character is required in new password', type: 'boolean', default: false },
      password_symboldigit: { description: 'whether special symbol character or digit is required in new password', type: 'boolean', default: false },

    }, cfg_schema)
    super(id, cfg, cfg_schema, dependencies)
    // if(!this.config.always_enabled && require('msysFS').device()!=='phone') cfg.disabled = true
    this.module_statuses = {}
    this.mode = 'user'
  } // constructor

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * initialization of GUI components
  */
  initWindow() {
    this.loadForm(require('./mmodAppWall.json'))
    this.setupItems()

    this.el.find('@behavior').options = this.config.behaviors
    this.el.find('>mele-body').onclick = (event)=>{
      if(_.toLower(event.target.tagName)==='mele-body') {
        this.publish('#ME', 'mmClose')
      }
    }
    this.mmStart()
    this.subscribe('#WM')
  } // initWindow

  setupItems() {
    this.el.forAll('@appwall', el=>{
      let items
      if(this.mode==='admin') {
        items = this.config.admin_modules.concat(['mmodReconnect', 'mmodModeUser'])
      }else{
        items = this.config.modules
        if(this.config.admin_modules.length>0) items=items.concat(['mmodModeAdmin'])
      }
      el.items = items

      this.hideHiddenModules(el)
      el.header='<mele-image key="logo" icon="mosy_m" type="toolbar"></mele-image><span key="app_name">'+MOSY_APP.replace(/^m/, '')+'</span>'
    })
  } // setupItems

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Sends by Window Manager when top full-screen window on any screen changes. Sends directly to that window and to #WM queue.
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {string} [params.id]
  * @param {integer} [params.screen]
  */
  mmWindowFocused(sender, params) {
    if(this.mode==='admin') this.last_admin_module = params.id
    else this.last_user_module = params.id
    this.el.forAll('@appwall', el=>{
      el.selected = [params.id]
    })
  } // mmWindowFocused

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * information on changed user authorization; user_id is empty if not logged
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {string} [params.app_id] - unique application ID
  * @param {integer} [params.user_id] - user's unique ID
  * @param {string} [params.login] - user's login name
  * @param {string} [params.callsign] - user's callsign
  * @param {object} [params.roles] - roles assigned to user
  * @param {string} [params.privatekey] - user's private key encrypted by his password
  * @param {string} [params.error] - error message if login failed
  */
  mmUserLogged(sender, params) {
    super.mmUserLogged(sender, params)
    const login = this.login = _.toString(params.login)
    const callsign = this.callsign = _.toString(params.callsign)

    if(params.user_id) {
      mfs.setItem('last_login', params.login)
    }
    if(params.callsign) {
      mfs.setItem('last_callsign', params.callsign)
    }
    this.isLogged = Boolean(params.user_id)
    // refresh username/callsign in affected fields and set up login buttons according to current login state
    this.el.forAll('@loginchange', el=>{
      el.label = callsign || 'No callsign'
      el.label2 = login || 'Not logged'
    })
    this.el.forAll('mele-input@login', el=>{
      el.value = login || mfs.getItem('last_login') || ''
      el.disabled=!!login
    })
    this.el.forAll('mele-input@password', el=>{ el.hidden=!!login })
    this.el.forAll('mele-input@callsign', el=>{
      el.value = callsign || mfs.getItem('last_callsign') || ''
      el.disabled=!!callsign
      el.format = login ? '\\w' : ''
    })

    this.el.forAll('mele-button@log_in', el=>{
      el.hidden=!!login
    })
    this.el.forAll('mele-button@sign_in', el=>{
      el.hidden=!!callsign || !login
    })
    this.el.forAll('mele-button@log_out', el=>{ el.hidden=!login })
    this.el.forAll('mele-button@sign_out', el=>{ el.hidden=!callsign })
  } // mmUserLogged

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * notification on changed user status
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {string} [params.app_id] - unique application ID
  * @param {string} params.status - new status
  * @param {string} [params.info] - additional info about status change
  */
  mmUserStatus(sender, params) {
    super.mmUserStatus(sender, params)
    this.el.find('@status').value = params.status
  } // mmUserStatus

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Request to change application color theme
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {string} [params.theme] - desired color theme
  */
  mmSetTheme(sender, params) {
    this.el.forAll('@theme', el=>{ el.iconleft='sys.theme.'+params.theme })
    super.mmSetTheme(sender, params)
  } // mmSetTheme

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Request to change application behavior
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {string} [params.behavior] - desired behavior
  */
  mmSetBehavior(sender, params) {
    const behavior = this.behavior = super.mmSetBehavior(sender, params)
    if(behavior==='desktop') {
      this.publish('msysWM', 'mmWindowSet', { type: 'none' })
    }else if(behavior!=='phone') {
      this.el.forAll('@behavior', el=>{ el.value=behavior })
    }
  } // mmSetBehavior

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Ask module to open its window. The action is module specific, e.g. `mmodAppWall` shows/hides itself when receiving `mmOpen`/`mmClose` messages
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {string} [params.view] - view to become active
  * @param {string} [params.id] - record to be displayed
  * @param {boolean} [params.replace] - indicates that current breadcrumb item shall be replaced
  */
  mmOpen(sender, params) {
    if(this.behavior==='desktop') return
    this.el.active = 0
    this.publish('msysWM', 'mmWindowSet', { type: 'float', x: '0', y: 0, width: '100%', height: '100%', order: 1 })
  } // mmOpen

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Ask module to close its window. The action is module specific, e.g. `mmodAppWall` shows/hides itself when receiving `mmOpen`/`mmClose` messages
  * @param {object} sender - Sender of the message
  * @param {object} params - not used
  */
  mmClose(sender, params) {
    this.publish('msysWM', 'mmWindowSet', { type: 'float', x: '-0', y: 0, width: '0', height: '100%', order: 1 })
  } // mmClose

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Sends by msvcSkyPhone when HW button is pressed
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {string} params.key - key attribute of the button
  */
  mmHWButton(sender, params) {
    if(this.isLogged && (params.key==='menu' || params.key==='skymain')) {
      this.publish('#ME', (parseInt(this.el.width) === 0) ? 'mmOpen' : 'mmClose')
    }
  } // mmHWButton

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Sends by <mele-list> when selected item change
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {string} [params.holder] - key attribute of the list
  * @param {string} params.selected - key attribute of the selected items separated by ∫
  * @param {string} [params.old] - key attribute of previously selected item
  * @param {boolean} [params.interactive] - indicates if this was user activity
  */
  mmListSelected(sender, params) {
    if(!params.interactive) return
    if(params.holder==='appwall' && params.interactive) {
      if(params.selected==='mmodReconnect') {
        this.publish('msysPeeringClient', 'mmPeeringDiscover')
      }else if(params.selected==='mmodModeAdmin') {
        this.publish('#ALLWITHME', 'mmMode', { mode: 'admin' })
      }else if(params.selected==='mmodModeUser') {
        this.publish('#ALLWITHME', 'mmMode', { mode: 'user' })
      }else {
        this.publish('msysWM', 'mmWindowFocus', { id: params.selected })
      }
      this.publish('#ME', 'mmClose')
    }
  } // mmListSelected

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Sends by &lt;mele-input> to parent module when input value changed
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {string} [params.holder] - key attribute of input's parent container
  * @param {string} params.input - key attribute of the input element changed
  * @param {string} [params.value] - new value
  * @param {string} [params.old] - old value
  * @param {boolean} [params.invalid] - if true, indicates the value is not according to given format
  * @param {boolean} [params.interactive] - indicates if this was user activity
  */
  mmValueChanged(sender, params) {
    if(!params.interactive) return
    const { input, value } = params
    if(!value) return
    if(input==='status') {
      this.publish('msysUser', 'mmUserStatus', { status: value })
      this.publish('#ME', 'mmClose')
    }else if(input==='behavior') {
      this.publish('#ALLWITHME', 'mmSetBehavior', { behavior: value })
      this.publish('#ME', 'mmClose')
    }
  } // mmValueChanged

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Sends by &lt;mele-button> to parent module when a button is clicked
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {string} [params.holder] - key attribute of button's parent container
  * @param {string} params.button - key attribute of the button
  * @param {integer} [params.count] - number of times button was clicked
  * @param {boolean} [params.long] - shows if press was long or not
  */
  mmButtonClicked(sender, params) {
    const { holder, button } = params

    if(holder==='appwall') {
      if(button==='close') {
        this.publish('#ME', 'mmClose')
      }else if(button==='theme') {
        const new_theme = /day/.test(this.el.find('@theme').iconleft) ? 'night' : 'day'
        this.publish('#ALLWITHME', 'mmSetTheme', { theme: new_theme })
        this.publish('#ME', 'mmClose')
      }else if(button==='loginchange') {
        this.el.active = 1
      }else if(button==='changepwd') {
        this.modalChangePassword()
      }
    }else if(holder==='loginview') {
      if(button==='log_out') {
        this.logoutUser()
        this.publish('#ME', 'mmClose')
      }else if(button==='sign_out') {
        this.publish('msysUser', 'mmUserLogin', { callsign: '' })
      }
      this.el.forAll('@password', el=>{ el.value='' })
      this.el.active=0
    }
  } // mmButtonClicked

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Status of the listener
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {string} [params.status]
  */
  mmStatus(sender, params) {
    this.module_statuses[sender.listener_id] = params.status
    this.el.forAll('@appwall', el=>{
      const elItem = el.getItemElement(sender.listener_id)
      if(elItem) elItem.keyChanged()
      this.hideHiddenModules(el)
    })
  } // mmStatus

  hideHiddenModules(elList) {
    elList.getItemElements().forEach(el=>{
      const status = this.module_statuses[el.key]
      el.setAttribute('status', status||'ok')
    })
    let elLastGroup = null; let elLastGroupHidden = true
    const win = this.el.ownerDocument.defaultView
    elList.getItemElements().forEach(el=>{
      if(el.key) {
        if(elLastGroupHidden) {
          const hidden = win.getComputedStyle(el).display==='none'
          if(!hidden) elLastGroupHidden=false
        }
      }else{
        if(elLastGroup) elLastGroup.setAttribute('status', elLastGroupHidden?'hidden':'')
        elLastGroup = el
        elLastGroupHidden = true
      }
    })
    if(elLastGroup) elLastGroup.setAttribute('status', elLastGroupHidden?'hidden':'')
  } // hideHiddenModules

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Opens modal dialog for password change
  */
  modalChangePassword() {
    const modal = this.modalOpen(require('./mmodAppWall_changepwd.json'))
    const pwd0 = modal.find('@password')
    const pwd1 = modal.find('@newpassword')
    const pwd2 = modal.find('@newpassword2')
    const cfg = this.config
    pwd1.validateFormat = function() {
      pwd2.validate() // on change of pwd1, check also pwd2
      const value = String(this.value??'')
      const valid =
      value.length>=cfg.password_minlength &&
      (!cfg.password_lowercase || /[a-z]/.test(value)) &&
      (!cfg.password_uppercase || /[A-Z]/.test(value)) &&
      (!cfg.password_digit || /[0-9]/.test(value)) &&
      (!cfg.password_letter || /[a-zA-Z]/.test(value)) &&
      (!cfg.password_letterdigit || /[a-zA-Z0-9]/.test(value)) &&
      (!cfg.password_symbol || new RegExp('['+'!@#$%^&*()_+{}:"|<>?~,./;\'\\[]-='.replace(/(.)/g, '\\$1')+']').test(value)) &&
      (!cfg.password_symboldigit || new RegExp('[0-9'+'!@#$%^&*()_+{}:"|<>?~,./;\'\\[]-='.replace(/(.)/g, '\\$1')+']').test(value))

      if(!valid) {
        this.formaterror = 'Your password does not meet security criteria'
        return false
      }

      if(value === String(pwd0.value??'')) {
        this.formaterror = 'New password cannot be the same as old password'
        return false
      }

      return true
    }
    pwd2.validateFormat = ()=>{
      return String(pwd1.value??'')===String(pwd2.value??'')
    }
  } // modalChangePassword

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Sent by &lt;mele-container> or derived class to parent module when form is submitted
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {string} params.holder - key attribute of the form
  * @param {string} [params.button] - key attribute of the button
  * @param {string} [params.datakey] - key attribute of the content
  * @param {object} params.values - hash of key:value of contained editable inputs
  * @param {boolean} [params.invalid] - true if any of form's inputs is invalid
  * @param {boolean} [params.conflict] - true if any of form's inputs has conflict
  */
  async mmFormSubmit(sender, params) {
    if(params.holder==='loginview') {
      this.publish('msysUser', 'mmUserLogin', this.el.find('@loginview').getValues())
      this.el.active=0
      this.el.forAll('@password', el=>{ el.value='' })
    } else if(params.holder==='changepwd') {
      this.modalClose('changepwd')
      this.modalWait('{{Please wait}}...', 'Change password')
      try{
        await this.request('msysUser', 'mmUserPasswordChange', _.pick(params.values, ['password', 'newpassword']), { resolve: 'mmSuccess', timeout: 20000 })
        this.modalInfo('Password has been changed', 'Change password')
      }catch(e) {
        this.modalError('Error changing password', 'Change password')
      }
    }
  } // mmFormSubmit

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * change of application mode
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {string} params.mode - mode to be set
  */
  mmMode(sender, params) {
    if(this.mode===params.mode) return
    this.mode = params.mode
    this.setupItems()
  } // mmMode
} // mmodAppWall
